import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { ClientRankingData } from '@/types/financial';
import { SensitiveValue } from '@/components/privacy/SensitiveText';

interface ClientRankingTableProps {
  clients: ClientRankingData[]
}

export function ClientRankingTable({ clients }: ClientRankingTableProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  };

  // Calculate margin percentage: (resultado / receitas) * 100
  const calculateMargin = (resultado: number, receitas: number): number => {
    if (!receitas || receitas === 0 || !isFinite(receitas)) return 0
    if (!resultado || !isFinite(resultado)) return 0
    return (resultado / receitas) * 100
  };

  // Validate clients is an array, fallback to empty array if not
  const clientsArray = Array.isArray(clients) ? clients : []

  // Sort clients by receitas descending and filter out invalid data
  const sortedClients = [...clientsArray]
    .filter(client => client.receitas && isFinite(client.receitas) && client.receitas > 0)
    .sort((a, b) => b.receitas - a.receitas)
    .slice(0, 10); // Show top 10 clients

  // LOG COMPREHENSIVE CLIENT MATRIX BREAKDOWN
  console.log('\n')
  console.log('╔═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════╗')
  console.log('║                                                    RANKING DE CLIENTES - FINANCIAL BREAKDOWN (Top 10)                                                                          ║')
  console.log('╚═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════════╝')
  console.log('')

  sortedClients.forEach((client, index) => {
    const receitas = client.receitas || 0
    const folhaActual = client.folhaActual || 0
    const custos = client.custos || 0
    const despesas = client.despesas || 0
    const impostos = client.impostos || 0
    const resultado = client.resultado || 0
    const proa = client.proa || 0

    // Calculate total costs for EBITDA (folhaActual + custos + despesas, WITHOUT impostos)
    const totalCosts = folhaActual + custos + despesas

    // Calculate verification
    const calculatedResultado = receitas - totalCosts
    const match = Math.abs(resultado - calculatedResultado) < 1 ? '✓' : '✗'

    const margin = calculateMargin(resultado, receitas)
    const performance = proa > 0 ? (receitas / proa * 100) : 0

    console.log(`\n${index + 1}. ${client.clientName}`)
    console.log('┌─────────────────────────┬──────────────────┬─────────────┐')
    console.log('│ Metric                  │      Value       │  % Receita  │')
    console.log('├─────────────────────────┼──────────────────┼─────────────┤')
    console.log(`│ Receita                 │ ${receitas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(16)} │ ${(100).toFixed(1).padStart(9)}% │`)
    console.log('├─────────────────────────┼──────────────────┼─────────────┤')
    console.log(`│ (-) Folha Actual        │ ${folhaActual.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(16)} │ ${(folhaActual / receitas * 100).toFixed(1).padStart(9)}% │`)
    console.log(`│ (-) Custos Diretos      │ ${custos.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(16)} │ ${(custos / receitas * 100).toFixed(1).padStart(9)}% │`)
    console.log(`│ (-) Custos ADM          │ ${despesas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(16)} │ ${(despesas / receitas * 100).toFixed(1).padStart(9)}% │`)
    console.log('├─────────────────────────┼──────────────────┼─────────────┤')
    console.log(`│ (=) TOTAL COSTS         │ ${totalCosts.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(16)} │ ${(totalCosts / receitas * 100).toFixed(1).padStart(9)}% │`)
    console.log('├─────────────────────────┼──────────────────┼─────────────┤')
    console.log(`│ (=) EBITDA              │ ${resultado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(16)} │ ${margin.toFixed(1).padStart(9)}% │ ${match}`)
    console.log('├─────────────────────────┼──────────────────┼─────────────┤')
    console.log(`│     Impostos (info)     │ ${impostos.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(16)} │ ${(impostos / receitas * 100).toFixed(1).padStart(9)}% │`)
    console.log('├─────────────────────────┼──────────────────┼─────────────┤')
    console.log(`│ PROA Target             │ ${proa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(16)} │ ${(proa / receitas * 100).toFixed(1).padStart(9)}% │`)
    console.log(`│ Performance vs PROA     │     ${performance.toFixed(1).padStart(10)}% │             │`)
    console.log('└─────────────────────────┴──────────────────┴─────────────┘')
  })

  console.log('\n')

  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 card-hover shadow-monumenta h-full flex flex-col">
      <div className="mb-4">
        <h3 className="text-white font-display text-xl font-semibold">Detalhamento por Cliente</h3>
      </div>

      <div className="overflow-auto flex-1">
        {sortedClients.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-brand-gray font-body text-sm">Nenhum cliente encontrado</p>
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 bg-brand-dark-light">
              <TableRow className="border-brand-dark-lighter">
                <TableHead className="w-[30%] text-brand-gray font-display">Cliente</TableHead>
                <TableHead className="text-right text-brand-gray font-display">Receita</TableHead>
                <TableHead className="text-right text-brand-gray font-display">Custo</TableHead>
                <TableHead className="text-right text-brand-gray font-display">Folha</TableHead>
                <TableHead className="text-right text-brand-gray font-display">Imposto</TableHead>
                <TableHead className="text-right text-brand-gray font-display">Despesa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedClients.map((client, index) => {
                return (
                  <TableRow
                    key={client.clientCode || index}
                    className="cursor-pointer hover:bg-brand-dark-lighter/50 border-brand-dark-lighter transition-colors"
                  >
                    <TableCell className="text-white font-body">{client.clientName}</TableCell>
                    <TableCell className="text-right text-white font-body">
                      <SensitiveValue mask="R$ ••••••">
                        {formatCurrency(client.receitas || 0)}
                      </SensitiveValue>
                    </TableCell>
                    <TableCell className="text-right text-white font-body">
                      <SensitiveValue mask="R$ ••••••">
                        {formatCurrency(client.custos || 0)}
                      </SensitiveValue>
                    </TableCell>
                    <TableCell className="text-right text-brand-gray font-body">-</TableCell>
                    <TableCell className="text-right text-brand-gray font-body">-</TableCell>
                    <TableCell className="text-right text-brand-gray font-body">-</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
